import React, { useEffect, useRef } from 'react';

// Cloudflare Turnstile — invisible CAPTCHA for the public lead forms.
//
// Renders nothing when VITE_TURNSTILE_SITE_KEY is unset, so the site works
// unchanged until the keys are configured. The server side is the real gate
// (api/lib/spam-shield.ts); this only produces the token.

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

interface TurnstileApi {
  render: (
    _el: HTMLElement,
    _options: {
      sitekey: string;
      callback: (_token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      size?: 'normal' | 'flexible' | 'compact';
      appearance?: 'always' | 'execute' | 'interaction-only';
    }
  ) => string;
  remove: (_widgetId: string) => void;
  reset: (_widgetId: string) => void;
}

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(
      SCRIPT_ID
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('load failed'))
      );
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('load failed'));
    document.head.appendChild(script);
  });
}

interface TurnstileWidgetProps {
  /** Receives the token, or null when the current one expires. */
  onVerify: (_token: string | null) => void;
  /**
   * Called when no token is coming at all — the script is blocked, or the
   * widget errored. Distinct from onVerify(null), which means "expired, a new
   * token is on its way": the caller must be able to unblock its submit button
   * for the first case without treating the form as verified.
   */
  onUnavailable?: () => void;
  /**
   * Change this to make the widget issue a fresh token.
   *
   * Turnstile tokens are single-use and short-lived, so after a submission is
   * rejected the token in hand is already spent — retrying with it fails
   * again, forever. Bumping this lets the caller break that loop without a
   * page reload.
   */
  resetSignal?: number;
  className?: string;
}

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  onVerify,
  onUnavailable,
  resetSignal = 0,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: token => onVerifyRef.current(token),
          // The widget could not run — no token is coming.
          'error-callback': () => onUnavailableRef.current?.(),
          // The token aged out; Turnstile issues a fresh one.
          'expired-callback': () => onVerifyRef.current(null),
          theme: 'dark',
          size: 'flexible',
        });
      })
      .catch(() => {
        // Script blocked (ad blocker) or Cloudflare unreachable. Report it as
        // unavailable rather than as an expired token, so the caller can
        // re-enable its submit button instead of leaving the visitor staring
        // at a dead form.
        onUnavailableRef.current?.();
      });

    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          // Widget already torn down with the DOM node.
        }
      }
    };
  }, [siteKey]);

  useEffect(() => {
    // Skip the initial render: the widget solves on mount by itself.
    if (!resetSignal) return;
    const id = widgetIdRef.current;
    if (!id || !window.turnstile) return;
    try {
      window.turnstile.reset(id);
    } catch {
      // Nothing to reset yet; the mount effect will produce a token.
    }
  }, [resetSignal]);

  if (!siteKey) return null;
  return <div ref={containerRef} className={className} />;
};

export default TurnstileWidget;
